import { supabase } from '../../supabaseClient';
import { getContractStatus } from './contractStatus';

/**
 * Load all companies with their contract history in a single query
 * Uses Supabase join to avoid N+1 queries
 * @param {Object} options - Query options
 * @param {number} options.userId - User ID for filtering (optional)
 * @param {boolean} options.isSuperAdmin - Whether user is super admin
 * @returns {Promise<Array>} Array of companies with contract status
 */
export const loadCompaniesWithContracts = async ({ userId, isSuperAdmin }) => {
  let query;

  if (isSuperAdmin) {
    // Super admins see all companies with their contract history
    query = supabase
      .from('Company')
      .select(`
        *,
        ContractHistory (
          id,
          company_id,
          start_date,
          end_date,
          notes,
          created_at,
          created_by,
          deleted_at,
          deleted_by
        )
      `)
      .is('deleted_at', null);
  } else {
    // Regular users see only their companies WITHOUT contract history
    query = supabase
      .from('UserCompany')
      .select(`
        type,
        company (
          *
        )
      `)
      .eq('user', userId)
      .is('deleted_at', null)
      .is('company.deleted_at', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error loading companies with contracts:', error);
    return [];
  }

  // Process the data
  let companies;

  if (isSuperAdmin) {
    companies = data;
  } else {
    // Extract companies from UserCompany relation
    companies = data
      .filter(({ company }) => company && company.active)
      .map(({ company, type }) => ({ ...company, type }));
  }

  // Only calculate contract status for super admins
  if (isSuperAdmin) {
    const companiesWithStatus = companies.map((company) => {
      // Filter out deleted contract history entries
      const contractHistory = (company.ContractHistory || []).filter(
        (contract) => !contract.deleted_at
      );

      // Calculate contract status (pass company active status)
      const contractStatus = getContractStatus(contractHistory, company.active);

      return {
        ...company,
        contractHistory,
        contractStatus,
      };
    });

    return companiesWithStatus;
  }

  // Regular users don't see contract status
  return companies;
};

/**
 * Load a single company with contract history
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} Company with contract history
 */
export const loadCompanyWithContracts = async (companyId) => {
  const { data, error } = await supabase
    .from('Company')
    .select(`
      *,
      ContractHistory (
        id,
        company_id,
        start_date,
        end_date,
        notes,
        created_at,
        created_by,
        deleted_at,
        deleted_by
      )
    `)
    .eq('id', companyId)
    .is('deleted_at', null)
    .single();

  if (error) {
    console.error('Error loading company with contracts:', error);
    return null;
  }

  // Filter out deleted contract history
  const contractHistory = (data.ContractHistory || []).filter(
    (contract) => !contract.deleted_at
  );

  // Calculate contract status (pass company active status)
  const contractStatus = getContractStatus(contractHistory, data.active);

  return {
    ...data,
    contractHistory,
    contractStatus,
  };
};
